/**
 * captureSource — abstracts microphone capture across the web (MediaRecorder)
 * and Capacitor native (capacitor-voice-recorder) so the same useLiveRecording
 * pipeline keeps streaming chunks regardless of platform.
 *
 * The web path is the proven 30-second rotating MediaRecorder pattern: every
 * cycle a fresh recorder is started against the live MediaStream, the prior
 * recorder is stopped, and its `onstop` flushes a complete-WebM blob. This
 * keeps each chunk decodable standalone (header + clusters), which is what
 * the gateway/ffmpeg wants.
 *
 * The native path runs the same 30-second cycle but uses
 * capacitor-voice-recorder's start/stop API. Each cycle emits a complete
 * AAC blob from the plugin. The brief (~50–200ms) gap between stop and the
 * next start matches the existing OVERLAP_MS=0 web behavior.
 *
 * Importantly, the native plugin keeps recording when the screen is locked
 * or the app is backgrounded (iOS UIBackgroundModes:audio + Android
 * foreground service inside the plugin), which is exactly why we wired
 * Siri/Assistant deep-links to /quick-record in the first place.
 */

import { Capacitor } from "@capacitor/core";
import { VoiceRecorder } from "capacitor-voice-recorder";
import { requestMicrophonePermission } from "@/services/audio/micPermission";

/**
 * Marker error so the recording UI can distinguish "user blocked the mic
 * and needs to flip a settings toggle" from generic capture failures
 * (mic in use by another app, codec init failure, etc).
 */
export class MicrophonePermissionDeniedError extends Error {
  readonly code = "MIC_PERMISSION_DENIED" as const;
  constructor() {
    super("Microphone permission denied");
    this.name = "MicrophonePermissionDeniedError";
  }
}

/**
 * Browsers expose `navigator.mediaDevices.getUserMedia` only on secure
 * origins. Mobile browsers in particular silently treat HTTP pages as
 * "no mic device" — without this guard the recording UI shows a generic
 * "couldn't start capture" error and clinicians on a non-HTTPS preview
 * URL can't tell what's wrong. This marker error lets the UI surface
 * "open this page over HTTPS" instead.
 */
export class MicrophoneInsecureContextError extends Error {
  readonly code = "MIC_INSECURE_CONTEXT" as const;
  constructor() {
    super(
      "Microphone access requires HTTPS. Open this page over a secure connection (https://) and try again.",
    );
    this.name = "MicrophoneInsecureContextError";
  }
}

export interface CaptureChunk {
  blob: Blob;
  mimeType: string;
  index: number;
}

/**
 * A partial view of the cycle currently being recorded: every timeslice
 * captured so far in this window, concatenated. Because the first
 * timeslice carries the container header, a prefix of the slices is
 * itself a valid, standalone-decodable file — that's what lets one
 * recorder feed the fast-preview transcriber without a second recorder
 * fighting it for the MediaStream.
 */
export interface CapturePreview {
  blob: Blob;
  mimeType: string;
  /** Index the in-progress cycle will be emitted under when it completes. */
  index: number;
}

export interface StartCaptureOptions {
  cycleMs: number;
  startingIndex: number;
  /** Live read; used so the cycle loop can suppress chunks recorded while
   *  the user paused the consult. */
  isPaused: () => boolean;
  /** Called once per completed cycle. May be async — the next cycle starts
   *  regardless, so the handler should not block on slow uploads. */
  onChunk: (chunk: CaptureChunk) => Promise<void> | void;
  /** Called on permission denial / mic failure. The pipeline must tear
   *  itself down; useLiveRecording surfaces this through `error`. */
  onFatalError: (err: unknown) => void;
  /** Optional fast-preview sink. When set (with `previewIntervalMs`), the
   *  SAME recorder emits a growing prefix of the in-flight cycle at that
   *  cadence. Web-only: native capture exposes no partial data. */
  onPreview?: (preview: CapturePreview) => void;
  previewIntervalMs?: number;
}

export interface CaptureSource {
  pause(): Promise<void>;
  resume(): Promise<void>;
  stop(): Promise<void>;
  /** Web-only. Returns the live MediaStream so callers can wire an
   *  AnalyserNode for the waveform visualizer. Returns null on native —
   *  the visualizer falls back to a flat / pulse animation there. */
  getStream(): MediaStream | null;
}

export async function startCapture(
  opts: StartCaptureOptions,
): Promise<CaptureSource> {
  if (Capacitor.isNativePlatform()) {
    return startNativeCapture(opts);
  }
  return startWebCapture(opts);
}

// ─── Web (MediaRecorder rotating-chunk) ─────────────────────────────────────

const WEB_TIMESLICE_MS = 1_000;
/** Below this a WebM/MP4 container is header fragments, not audio. */
const MIN_DECODABLE_CHUNK_BYTES = 512;
const WEB_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
];

/** Best supported audio mime for a MediaRecorder on this browser.
 *  Exported for the fast-preview recorder (useLiveRecording), which runs
 *  a second rotating recorder against the same MediaStream and must agree
 *  on the container format. */
export function pickWebMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const m of WEB_MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

async function startWebCapture(
  opts: StartCaptureOptions,
): Promise<CaptureSource> {
  // Pre-flight checks for the most common "mic doesn't work on phone"
  // root causes. Mobile browsers expose `navigator.mediaDevices` only
  // on secure contexts (https / localhost); on HTTP it's undefined and
  // the bare `getUserMedia` call below throws an opaque TypeError that
  // surfaces as a generic "couldn't start" toast in the UI. Surface a
  // clear, actionable error instead.
  if (
    typeof window !== "undefined" &&
    "isSecureContext" in window &&
    !window.isSecureContext
  ) {
    const insecure = new MicrophoneInsecureContextError();
    opts.onFatalError(insecure);
    throw insecure;
  }
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices ||
    typeof navigator.mediaDevices.getUserMedia !== "function"
  ) {
    const insecure = new MicrophoneInsecureContextError();
    opts.onFatalError(insecure);
    throw insecure;
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (err) {
    // Map a denial to our marker so the UI can offer the "enable in
    // browser site settings" path instead of a generic error toast.
    const name = (err as DOMException | undefined)?.name;
    if (name === "NotAllowedError" || name === "SecurityError") {
      const denied = new MicrophonePermissionDeniedError();
      opts.onFatalError(denied);
      throw denied;
    }
    throw err;
  }

  const mime = pickWebMime();
  let chunkIndex = opts.startingIndex;
  let currentRecorder: MediaRecorder | null = null;
  let cycleTimer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  // `pickWebMime()` only ever returns WebM/Ogg candidates, so `undefined`
  // means this browser can't produce either — in practice Safari/iOS,
  // which gives us fragmented MP4 instead. That single fact drives two
  // behaviours below, because MP4 does NOT survive the tricks WebM does.
  const isAppendable = Boolean(mime);

  const createRecorder = (): MediaRecorder => {
    const localChunks: Blob[] = [];
    const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    // Report what the recorder ACTUALLY produced. Hard-coding "audio/webm"
    // handed ffmpeg MP4 bytes under a WebM label on every iPhone.
    const actualMime = r.mimeType || mime || 'audio/webm';
    // The ONLY writer of `localChunks`. Everything below reads from it:
    // the cycle flush and the preview fan-out both just concatenate what
    // this handler has buffered. Without it nothing is ever captured and
    // the consult ends with "No audio recorded".
    r.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) localChunks.push(ev.data);
    };
    // Fast-preview fan-out from THIS recorder. A second MediaRecorder on
    // the same MediaStream is not an option: WebKit tears the first one
    // down when the second attaches, which silently killed the canonical
    // (note-producing) pipeline on iPhones while preview kept running.
    // One recorder, many listeners — the preview is just a prefix of the
    // slices this recorder has already buffered, and a prefix is valid
    // because slice 0 carries the container header.
    let previewTimer: ReturnType<typeof setInterval> | null = null;
    let lastPreviewedSlices = 0;
    const stopPreview = () => {
      if (previewTimer) {
        clearInterval(previewTimer);
        previewTimer = null;
      }
    };
    // Preview takes a PREFIX of the buffered slices. That's only a valid
    // file for WebM, where slice 0 carries the header and later slices are
    // appendable clusters. A partial MP4 is undecodable, so on Safari the
    // preview is skipped rather than shipping garbage to the STT.
    if (opts.onPreview && opts.previewIntervalMs && isAppendable) {
      previewTimer = setInterval(() => {
        if (stopped || opts.isPaused()) return;
        // Nothing new since the last preview — don't re-transcribe an
        // identical prefix.
        if (localChunks.length === lastPreviewedSlices) return;
        lastPreviewedSlices = localChunks.length;
        const blob = new Blob(localChunks, { type: actualMime });
        if (blob.size < MIN_DECODABLE_CHUNK_BYTES) return;
        try {
          opts.onPreview?.({ blob, mimeType: actualMime, index: chunkIndex });
        } catch {
          /* preview is ephemeral by design — never let it break capture */
        }
      }, opts.previewIntervalMs);
    }
    r.onstop = async () => {
      stopPreview();
      // NB: we intentionally do NOT bail when paused here. `stop()`/`pause()`
      // both flush the current recorder, and the audio captured right up to
      // that click is real speech the doctor expects transcribed — dropping
      // it is the "transcript lost on pause unless you wait" bug. The hook's
      // `acceptChunksRef` decides whether to keep the flushed tail.
      if (localChunks.length === 0) return;
      const blob = new Blob(localChunks, { type: actualMime });
      // A container this small holds no decodable audio (1s of Opus is
      // multiple KB). Shipping one is worse than dropping it: it becomes
      // the session's chunk 0, ffmpeg fails to demux the concatenated
      // stream, and the whole consult ends with "Audio conversion
      // failed" — no transcript, no note. Don't burn an index on it
      // either, so the first real chunk still lands at 0.
      if (blob.size < MIN_DECODABLE_CHUNK_BYTES) {
        console.warn(
          `[captureSource:web] dropping degenerate ${blob.size}-byte chunk`,
        );
        return;
      }
      const idx = chunkIndex++;
      try {
        await opts.onChunk({ blob, mimeType: actualMime, index: idx });
      } catch (err) {
        // The hook's onChunk is best-effort (IDB enqueue + socket emit). A
        // throw here means both legs failed simultaneously, which is rare
        // but possible (private mode + offline). Don't tear down — the next
        // chunk may succeed; useLiveRecording's error state covers UX.
        // eslint-disable-next-line no-console
        console.warn("[captureSource:web] onChunk failed", err);
      }
    };
    // WebM: 1s timeslices, so a crash mid-cycle still leaves recoverable
    // audio and the preview has something to read.
    // MP4 (Safari): NO timeslice. MediaRecorder only finalises the moov
    // atom on stop, so concatenated fragments are not a playable file —
    // which is exactly why 700 KB of real audio came back "Audio
    // conversion failed". One blob per rotation, written at stop, is the
    // only decodable shape. Costs incremental durability within a cycle;
    // a note beats a live transcript.
    if (isAppendable) r.start(WEB_TIMESLICE_MS);
    else r.start();
    return r;
  };

  currentRecorder = createRecorder();

  cycleTimer = setInterval(() => {
    if (stopped) return;
    const old = currentRecorder;
    if (!old || old.state !== "recording") return;
    currentRecorder = createRecorder();
    try {
      old.stop();
    } catch {
      /* ignore — already stopped or in transition */
    }
  }, opts.cycleMs);

  const teardown = async () => {
    stopped = true;
    if (cycleTimer) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
    const r = currentRecorder;
    currentRecorder = null;
    // Await the final onstop → onChunk so the tail audio (captured right up to
    // the pause/stop click) is flushed to the gateway BEFORE we release the
    // mic. Callers (useLiveRecording) keep accepting chunks until this
    // resolves, so the last words of the consult aren't lost.
    if (r && r.state !== "inactive") {
      await new Promise<void>((resolve) => {
        const prev = r.onstop;
        r.onstop = (ev: Event) => {
          Promise.resolve(prev ? prev.call(r, ev) : undefined).finally(resolve);
        };
        try {
          r.stop();
        } catch {
          resolve();
        }
      });
    }
    stream.getTracks().forEach((t) => t.stop());
  };

  return {
    pause: teardown, // Mirror the existing web behavior: full mic release
    // on pause so the browser indicator drops. The hook re-acquires by
    // calling startCapture again on resume.
    async resume() {
      // No-op — the hook re-creates the source on resume.
    },
    stop: teardown,
    getStream() {
      return stopped ? null : stream;
    },
  };
}

// ─── Native (capacitor-voice-recorder stop/start cycle) ─────────────────────

async function startNativeCapture(
  opts: StartCaptureOptions,
): Promise<CaptureSource> {
  // Permission gate. Routes through the centralized helper so the
  // capture path and the wakeword/QuickRecord paths agree on what
  // counts as granted/denied/unavailable. A denial throws our marker
  // error; useLiveRecording's catch path uses `instanceof
  // MicrophonePermissionDeniedError` to render the "enable in Settings"
  // affordance instead of a generic capture-failure toast.
  const status = await requestMicrophonePermission();
  if (status !== "granted") {
    const err = new MicrophonePermissionDeniedError();
    opts.onFatalError(err);
    throw err;
  }

  let chunkIndex = opts.startingIndex;
  let stopped = false;
  // Re-entrancy guard: a slow stopRecording → onChunk → startRecording leg
  // can race with a stop() call from useLiveRecording. Drop late chunks
  // arriving after stop() so they don't show up in a future session.
  let teardownStarted = false;
  let cycleTimeout: ReturnType<typeof setTimeout> | null = null;

  // Kick off the very first recording. Each cycle = one start→stop pair.
  await VoiceRecorder.startRecording();

  const cycle = async () => {
    if (stopped || teardownStarted) return;
    cycleTimeout = null;

    let result: Awaited<ReturnType<typeof VoiceRecorder.stopRecording>> | null =
      null;
    try {
      result = await VoiceRecorder.stopRecording();
    } catch (err) {
      // stopRecording can fail if the user revoked permission mid-consult
      // or the OS killed the recorder (rare). Treat as fatal.
      opts.onFatalError(err);
      return;
    }

    // Immediately re-arm the next recording — the gap is what costs us
    // captured audio, so we want it as small as possible. Don't await
    // onChunk before re-arming.
    if (!stopped && !teardownStarted) {
      try {
        await VoiceRecorder.startRecording();
      } catch (err) {
        opts.onFatalError(err);
        return;
      }
      // Schedule the next cycle even if the chunk handler is slow.
      cycleTimeout = setTimeout(cycle, opts.cycleMs);
    }

    // Now ship the just-finished chunk. While paused, the hook expects no
    // chunk to flow — match the web path's onstop guard.
    const data = result?.value;
    if (!data?.recordDataBase64) return;
    if (opts.isPaused()) return;

    const blob = base64ToBlob(data.recordDataBase64, data.mimeType ?? "audio/aac");
    const idx = chunkIndex++;
    try {
      await opts.onChunk({
        blob,
        mimeType: data.mimeType ?? "audio/aac",
        index: idx,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[captureSource:native] onChunk failed", err);
    }
  };

  cycleTimeout = setTimeout(cycle, opts.cycleMs);

  return {
    async pause() {
      // The native plugin supports pauseRecording natively — preserves the
      // current cycle's audio so resume continues the same file. We still
      // suppress mid-cycle chunk emission via opts.isPaused() so the hook
      // doesn't see audio that wraps the pause boundary.
      try {
        await VoiceRecorder.pauseRecording();
      } catch {
        /* not all OS versions support pause; teardown-on-pause is fine fallback */
      }
    },
    async resume() {
      try {
        await VoiceRecorder.resumeRecording();
      } catch {
        /* if the recording was torn down on pause, the hook will start a fresh source */
      }
    },
    async stop() {
      stopped = true;
      teardownStarted = true;
      if (cycleTimeout) {
        clearTimeout(cycleTimeout);
        cycleTimeout = null;
      }
      // Best-effort final stop — surface its chunk on the way out so the
      // tail of the consult reaches the gateway before end-recording fires.
      let final: Awaited<ReturnType<typeof VoiceRecorder.stopRecording>> | null =
        null;
      try {
        final = await VoiceRecorder.stopRecording();
      } catch {
        /* recorder already stopped or never started — ignore */
      }
      // Flush the tail even when paused — pause routes through stop() in the
      // hook, and the audio up to the pause click is real speech to transcribe
      // (fixes "transcript lost on pause"). The hook's acceptChunksRef gates it.
      const data = final?.value;
      if (data?.recordDataBase64) {
        const blob = base64ToBlob(
          data.recordDataBase64,
          data.mimeType ?? "audio/aac",
        );
        const idx = chunkIndex++;
        try {
          await opts.onChunk({
            blob,
            mimeType: data.mimeType ?? "audio/aac",
            index: idx,
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[captureSource:native] final onChunk failed", err);
        }
      }
    },
    getStream() {
      return null;
    },
  };
}

// ─── helpers ────────────────────────────────────────────────────────────────

function base64ToBlob(base64: string, mimeType: string): Blob {
  // atob produces a binary string; copy into a Uint8Array so the Blob owns
  // its own buffer.
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}
